import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  MessageSquare, X, Send, ThumbsUp, ThumbsDown, 
  Sparkles, Loader, Briefcase, Code, FolderGit2, Check,
  RotateCcw
} from 'lucide-react';

interface Message {
  id?: number;
  role: 'user' | 'assistant';
  content: string;
  helpful?: number | null;
}

const SUGGESTED_QUESTIONS = [
  "Quali sono le competenze principali di Walter?",
  "Che ruolo ha avuto in Candiani SpA?",
  "Di cosa si occupa come System Integrator?",
  "Quali database conosce meglio?",
  "Walter programma anche per il Cloud?",
  "Come gestisce la telemetria industriale (IoT/PLC)?",
  "Ha esperienza con le reti e la fibra ottica?",
  "Come posso contattare Walter per una proposta?",
  "Walter lavora in team o da solo?",
  "Quali framework frontend utilizza?",
  "Che tipo di progetti ha sviluppato con C#?",
  "Walter conosce anche Linux o solo Windows?",
  "Come affronta il problem solving?",
  "Può aiutare a modernizzare sistemi legacy?",
  "Qual è la sua esperienza con la tecnologia RFID?"
];

// Helper per estrarre 4 elementi casuali
const getRandomSuggestions = (): string[] => {
  const shuffled = [...SUGGESTED_QUESTIONS].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, 4);
};

export default function PortfolioChat() {
  const [isDisabled, setIsDisabled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [feedbackStatus, setFeedbackStatus] = useState<Record<number, 'up' | 'down'>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeIcon, setActiveIcon] = useState<'logo' | 'chat'>('logo');
  const [showTooltip, setShowTooltip] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMouseFar, setIsMouseFar] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatButtonRef = useRef<HTMLButtonElement>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const sessionInitializedRef = useRef(false);

  // Forza lo scroll automatico all'ultimo messaggio
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen, scrollToBottom]);

  // Verifica se la chat è abilitata all'avvio
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await fetch('/api/cv/chat/init?checkOnly=true');
        const data = await res.json();
        if (data.ok && data.disabled) {
          setIsDisabled(true);
        }
      } catch (err) {
        // Ignora
      }
    };
    checkStatus();
  }, []);

  // Pulsante semi-trasparente quando il mouse è lontano (solo dispositivi con mouse)
  useEffect(() => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const FAR_DISTANCE = 220;
    let frame = 0;
    const onMove = (e: MouseEvent) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const rect = chatButtonRef.current?.getBoundingClientRect();
        if (!rect) return;
        const dx = e.clientX - (rect.left + rect.width / 2);
        const dy = e.clientY - (rect.top + rect.height / 2);
        setIsMouseFar(Math.hypot(dx, dy) > FAR_DISTANCE);
      });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(frame);
    };
  }, []);

  // Gestione transizione icona (logo -> chat) e apparizione tooltip ad avvio pagina
  useEffect(() => {
    if (isDisabled) return;
    
    // Transizione logo -> chat dopo 4 secondi
    const iconTimer = setTimeout(() => {
      setActiveIcon('chat');
    }, 4000);

    // Mostra fumetto di benvenuto dopo 5.5 secondi e nascondilo dopo altri 6:
    // in seguito riappare solo al passaggio del mouse sul pulsante
    const tooltipTimer = setTimeout(() => {
      setShowTooltip(true);
    }, 5500);
    const tooltipHideTimer = setTimeout(() => {
      setShowTooltip(false);
    }, 11500);

    return () => {
      clearTimeout(iconTimer);
      clearTimeout(tooltipTimer);
      clearTimeout(tooltipHideTimer);
    };
  }, [isDisabled]);

  // Carica i suggerimenti casuali
  useEffect(() => {
    setSuggestions(getRandomSuggestions());
  }, []);

  // Logga l'errore in console e lo invia al server per la registrazione su DB
  const logClientError = useCallback(async (context: string, err: unknown) => {
    console.error(`[chat] ${context}:`, err);
    try {
      const message = err instanceof Error ? err.message : String(err);
      await fetch('/api/cv/chat/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ context, message: message.substring(0, 255) }),
      });
    } catch {
      // Se anche il logging fallisce, resta solo la console
    }
  }, []);

  // Invia telemetria degli eventi al server (solo a sessione inizializzata)
  const sendEvent = useCallback(async (eventType: string, eventValue: string) => {
    if (!sessionInitializedRef.current) return;
    try {
      await fetch('/api/cv/chat/event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventType, eventValue }),
      });
    } catch (err) {
      console.warn('[chat] Errore invio evento:', err);
    }
  }, []);

  // Inizializza la sessione (lazy: solo alla prima apertura della chat,
  // per non creare cookie e righe DB per visitatori che non la usano)
  const initChat = useCallback(async () => {
    if (sessionInitializedRef.current) return;
    try {
      const visitorSessionId = sessionStorage.getItem('tracking_session_id') || '';
      const referrer = document.referrer || '';
      const language = navigator.language?.split('-')[0] || 'it';
      const deviceType = /Mobi|Android|iPhone/i.test(navigator.userAgent) ? 'mobile' : 'desktop';

      const res = await fetch(
        `/api/cv/chat/init?visitorSessionId=${visitorSessionId}&referrer=${encodeURIComponent(referrer)}&language=${language}&deviceType=${deviceType}`
      );
      const data = await res.json();
      if (data.ok) {
        sessionInitializedRef.current = true;
        // Registra l'apertura che ha innescato l'init (persa da sendEvent perché la sessione non era pronta)
        sendEvent('chat_opened', 'floating_button');
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages);
          setIsReturning(true);

          // Ripristina lo stato dei feedback pregressi
          const existingFeedback: Record<number, 'up' | 'down'> = {};
          data.messages.forEach((msg: Message) => {
            if (msg.id && msg.helpful !== null && msg.helpful !== undefined) {
              existingFeedback[msg.id] = msg.helpful === 1 ? 'up' : 'down';
            }
          });
          setFeedbackStatus(existingFeedback);
        }
      }
    } catch (err) {
      logClientError('init', err);
    }
  }, [sendEvent, logClientError]);

  const handleResetChat = async () => {
    if (window.confirm("Vuoi iniziare una nuova conversazione? La cronologia attuale verrà cancellata.")) {
      try {
        await fetch('/api/cv/chat/reset', { method: 'POST' });
        setMessages([]);
        setIsReturning(false);
        setFeedbackStatus({});
        sessionInitializedRef.current = false;
        
        // Forza una nuova inizializzazione immediata
        const visitorSessionId = sessionStorage.getItem('tracking_session_id') || '';
        const referrer = document.referrer || '';
        const language = navigator.language?.split('-')[0] || 'it';
        const deviceType = /Mobi|Android|iPhone/i.test(navigator.userAgent) ? 'mobile' : 'desktop';

        const res = await fetch(
          `/api/cv/chat/init?visitorSessionId=${visitorSessionId}&referrer=${encodeURIComponent(referrer)}&language=${language}&deviceType=${deviceType}`
        );
        const data = await res.json();
        if (data.ok) {
          sessionInitializedRef.current = true;
          sendEvent('chat_reset', 'header_button');
        }
      } catch (err) {
        logClientError('reset', err);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      initChat();
    }
  }, [isOpen, initChat]);

  // Intercettore di conversioni globali sul portfolio (LinkedIn, GitHub, Mail, Contatti, Download CV)
  useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      let target = e.target as HTMLElement | null;
      while (target) {
        if (target.tagName === 'A' || target.tagName === 'BUTTON') {
          const href = target.getAttribute('href') || '';
          const id = target.id || '';
          const text = target.textContent?.toLowerCase() || '';

          // Download CV
          if (href.includes('cv') && href.toLowerCase().endsWith('.pdf') || id.includes('download') || text.includes('scarica cv') || text.includes('download cv')) {
            sendEvent('download_cv', href || text);
            break;
          }
          // GitHub
          if (href.includes('github.com')) {
            sendEvent('github_click', href);
            break;
          }
          // LinkedIn
          if (href.includes('linkedin.com')) {
            sendEvent('linkedin_click', href);
            break;
          }
          // Email
          if (href.startsWith('mailto:')) {
            sendEvent('email_click', href);
            break;
          }
          // Sezione Contatti
          if (href === '#contact' || id.includes('contact') || text.includes('contattami') || text.includes('scrivimi')) {
            sendEvent('contact_click', href || text);
            break;
          }
        }
        target = target.parentElement;
      }
    };

    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, [sendEvent]);

  // Intercetta i click sui link interni #hash del Markdown
  const handleMessageClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'A') {
      const href = target.getAttribute('href') || '';
      if (href.startsWith('#')) {
        e.preventDefault();
        const elementId = href.substring(1);
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          // Registra l'evento
          sendEvent('link_click', href);
          // Chiudi temporaneamente la chat per consentire la visualizzazione su mobile
          if (window.innerWidth < 768) {
            setIsOpen(false);
          }
        }
      }
    }
  };

  // Gestisce il feedback 👍/👎
  const handleFeedback = async (messageId: number, isHelpful: boolean) => {
    const value = isHelpful ? 1 : 0;
    try {
      const res = await fetch(`/api/cv/chat/message/${messageId}/feedback`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ helpful: value }),
      });
      if (res.ok) {
        setFeedbackStatus(prev => ({
          ...prev,
          [messageId]: isHelpful ? 'up' : 'down'
        }));
        sendEvent(isHelpful ? 'helpful_click' : 'unhelpful_click', String(messageId));
      }
    } catch (err) {
      logClientError('feedback', err);
    }
  };

  // Invia il messaggio
  const handleSendMessage = async (textToSend: string) => {
    if (!textToSend || textToSend.trim() === '' || isLoading) return;
    
    setInputMessage('');
    setIsLoading(true);
    setErrorMessage(null);

    const userText = textToSend.trim();

    // Aggiunge localmente il messaggio dell'utente
    setMessages(prev => [...prev, { role: 'user', content: userText }]);

    // Rinfresca le domande suggerite
    setSuggestions(getRandomSuggestions());

    try {
      const response = await fetch('/api/cv/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userText }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Errore del server');
      }

      // Prepara il messaggio dell'assistente per lo streaming
      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let completeText = '';
      let buffer = '';

      if (!reader) {
        throw new Error('Impossibile avviare il lettore di stream.');
      }

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const part of parts) {
          const lines = part.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const dataStr = line.slice(6).trim();
              if (dataStr === '[DONE]') continue;

              try {
                const parsed = JSON.parse(dataStr);
                
                if (parsed.text) {
                  completeText += parsed.text;
                  setMessages(prev => {
                    const newMessages = [...prev];
                    const lastMsg = newMessages[newMessages.length - 1];
                    if (lastMsg && lastMsg.role === 'assistant') {
                      lastMsg.content = completeText;
                    }
                    return newMessages;
                  });
                }

                if (parsed.done && parsed.messageId) {
                  // Aggiunge l'ID del messaggio per abilitare il feedback 👍/👎
                  setMessages(prev => {
                    const newMessages = [...prev];
                    const lastMsg = newMessages[newMessages.length - 1];
                    if (lastMsg && lastMsg.role === 'assistant') {
                      lastMsg.id = parsed.messageId;
                    }
                    return newMessages;
                  });
                }

                if (parsed.error) {
                  throw new Error(parsed.error);
                }
              } catch (e) {
                // Buffer incompleto o errore di parsing
              }
            }
          }
        }
      }

    } catch (err: any) {
      logClientError('stream', err);
      setErrorMessage(err.message || 'Errore durante la generazione della risposta.');
      setMessages(prev => {
        // Rimuove il messaggio dell'assistente vuoto e aggiunge quello di fallback
        const filtered = prev.filter((_, idx) => idx !== prev.length - 1);
        return [
          ...filtered,
          {
            role: 'assistant',
            content: 'In questo momento non riesco a rispondere. Puoi consultare direttamente il CV oppure contattare Walter.'
          }
        ];
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isDisabled) return null;

  return (
    <>
      {/* Stile CSS custom per effetto glow orbitale stile WBall */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes float-breathing {
          0%, 100% {
            box-shadow: 0 10px 25px -5px rgba(14, 165, 233, 0.4), 0 8px 10px -6px rgba(20, 184, 166, 0.4), 0 0 0 0px rgba(14, 165, 233, 0.3);
          }
          50% {
            box-shadow: 0 20px 35px -5px rgba(14, 165, 233, 0.6), 0 12px 20px -6px rgba(20, 184, 166, 0.6), 0 0 0 10px rgba(14, 165, 233, 0);
          }
        }
        .wball-chat-glow {
          animation: float-breathing 3s infinite ease-in-out;
        }
      `}} />

      {/* Pulsante Fluttuante Bottom-Right (Orb animata stile WBall con animazione di ingresso) */}
      <motion.div
        initial={{ scale: 0, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ 
          type: 'spring', 
          stiffness: 260, 
          damping: 20, 
          delay: 1.5 
        }}
        className="fixed bottom-6 right-6 z-50 flex flex-col items-end"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Fumetto Tooltip di attenzione */}
        <AnimatePresence>
          {(showTooltip || isHovered) && !isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 5, scale: 0.95 }}
              onClick={() => {
                setIsOpen(true);
                setShowTooltip(false);
                sendEvent('chat_opened', 'tooltip_click');
              }}
              className="mb-3 px-3 cursor-pointer py-2 bg-gradient-to-r from-sky-500 to-teal-500 text-white text-xs font-medium rounded-xl shadow-lg border border-sky-400/20 relative mr-2 select-none"
            >
              Chiedi a Walter AI! 💬
              {/* Triangolino del fumetto */}
              <div className="absolute bottom-[-6px] right-5 w-3 h-3 bg-teal-500 rotate-45 border-r border-b border-sky-400/20"></div>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          ref={chatButtonRef}
          onClick={() => {
            setIsOpen(!isOpen);
            setShowTooltip(false);
            setIsHovered(false);
            sendEvent(isOpen ? 'chat_closed' : 'chat_opened', 'floating_button');
          }}
          className={`relative flex items-center justify-center w-14 h-14 rounded-full text-white bg-gradient-to-tr from-sky-500 via-teal-500 to-emerald-500 wball-chat-glow hover:scale-105 active:scale-95 transition-[transform,opacity] duration-300 focus:outline-none group overflow-hidden ${isMouseFar && !isOpen && !showTooltip ? 'opacity-50' : 'opacity-100'}`}
          aria-label="Apri Walter AI"
        >
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0, scale: 0.5 }}
                animate={{ rotate: 0, opacity: 1, scale: 1 }}
                exit={{ rotate: 90, opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.2 }}
              >
                <X className="w-6 h-6" />
              </motion.div>
            ) : activeIcon === 'logo' ? (
              <motion.div
                key="logo"
                initial={{ rotate: 90, opacity: 0, scale: 0.5 }}
                animate={{ rotate: 0, opacity: 1, scale: 1 }}
                exit={{ rotate: -90, opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.3 }}
                className="w-7 h-7 flex items-center justify-center"
              >
                <img 
                  src="/Sigla_WR_animated.svg" 
                  alt="WR Logo" 
                  className="w-full h-full object-contain brightness-0 invert" 
                />
              </motion.div>
            ) : (
              <motion.div
                key="chat"
                initial={{ rotate: 90, opacity: 0, scale: 0.5 }}
                animate={{ rotate: 0, opacity: 1, scale: 1 }}
                exit={{ rotate: -90, opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.3 }}
              >
                <MessageSquare className="w-6 h-6" />
              </motion.div>
            )}
          </AnimatePresence>
        </button>
      </motion.div>

      {/* Finestra di Chat (Glassmorphism e Framer Motion) */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            ref={chatWindowRef}
            className="fixed bottom-24 right-6 w-[92vw] sm:w-[600px] h-[75vh] max-h-[600px] bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border border-gray-200/50 dark:border-gray-800/50 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200/30 dark:border-gray-800/30 bg-gradient-to-r from-sky-500/10 to-teal-500/10">
              <div className="flex items-center gap-3">
                {/* Avatar con pallino Online */}
                <div className="relative w-9 h-9 rounded-full bg-gradient-to-tr from-sky-400 to-teal-400 flex items-center justify-center text-white font-bold text-sm shadow-md">
                  W
                  <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white dark:border-gray-900 rounded-full"></span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm flex items-center gap-1.5">
                    Walter AI
                    <Sparkles className="w-3.5 h-3.5 text-teal-500 animate-pulse" />
                  </h3>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">Assistente virtuale basato su IA</p>
                </div>
              </div>
              
              <div className="flex items-center gap-1">
                {/* Nuova Chat / Reset */}
                {messages.length > 0 && (
                  <button
                    onClick={handleResetChat}
                    title="Inizia nuova chat"
                    className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-sky-500 dark:hover:text-sky-400 transition-all active:scale-95 duration-200"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                )}
                
                <button
                  onClick={() => {
                    setIsOpen(false);
                    sendEvent('chat_closed', 'header_close_button');
                  }}
                  className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messaggi */}
            <div 
              className="flex-1 overflow-y-auto px-5 py-4 space-y-4 select-text"
              onClick={handleMessageClick}
            >
              {/* Benvenuto Iniziale */}
              {messages.length === 0 && (
                <div className="space-y-4">
                  <div className="flex gap-2.5 max-w-[85%]">
                    <div className="flex-1 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-4 py-3 rounded-2xl rounded-tl-sm text-sm shadow-sm leading-relaxed border border-gray-200/20 dark:border-gray-700/20">
                      <p className="font-semibold text-teal-600 dark:text-teal-400 mb-1 flex items-center gap-1.5">
                        Ciao!
                      </p>
                      <p>
                        Sono <strong>Walter AI</strong>, l'assistente virtuale di Walter. Posso rispondere a domande sulla mia esperienza, sui progetti realizzati (es. Candiani SpA), sulle tecnologie che utilizzo e su come posso aiutarti a risolvere problemi software e hardware.
                      </p>
                    </div>
                  </div>

                  <div className="bg-sky-500/5 border border-sky-500/10 rounded-xl p-4 space-y-3">
                    <p className="text-xs font-semibold text-sky-600 dark:text-sky-400 uppercase tracking-wider">
                      Cosa ti interessa di Walter?
                    </p>
                    <div className="grid grid-cols-1 gap-2">
                      <button
                        onClick={() => {
                          sendEvent('suggestion_click', '💼 Esperienza lavorativa');
                          handleSendMessage("Parlami dell'esperienza lavorativa di Walter");
                        }}
                        className="flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-gray-800 hover:bg-sky-500/10 dark:hover:bg-sky-500/10 text-gray-700 dark:text-gray-200 hover:text-sky-600 dark:hover:text-sky-400 border border-gray-200 dark:border-gray-700 hover:border-sky-500/30 rounded-lg text-left text-xs font-medium transition-all duration-200 shadow-sm"
                      >
                        <Briefcase className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>💼 Esperienza lavorativa</span>
                      </button>
                      <button
                        onClick={() => {
                          sendEvent('suggestion_click', '🚀 Progetti realizzati');
                          handleSendMessage("Che tipo di progetti ha realizzato Walter?");
                        }}
                        className="flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-gray-800 hover:bg-sky-500/10 dark:hover:bg-sky-500/10 text-gray-700 dark:text-gray-200 hover:text-sky-600 dark:hover:text-sky-400 border border-gray-200 dark:border-gray-700 hover:border-sky-500/30 rounded-lg text-left text-xs font-medium transition-all duration-200 shadow-sm"
                      >
                        <FolderGit2 className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>🚀 Progetti realizzati (es. Candiani)</span>
                      </button>
                      <button
                        onClick={() => {
                          sendEvent('suggestion_click', '🛠 Competenze principali');
                          handleSendMessage("Quali sono le competenze tecniche di Walter?");
                        }}
                        className="flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-gray-800 hover:bg-sky-500/10 dark:hover:bg-sky-500/10 text-gray-700 dark:text-gray-200 hover:text-sky-600 dark:hover:text-sky-400 border border-gray-200 dark:border-gray-700 hover:border-sky-500/30 rounded-lg text-left text-xs font-medium transition-all duration-200 shadow-sm"
                      >
                        <Code className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>🛠 Competenze principali</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Messaggio Bentornato */}
              {messages.length > 0 && isReturning && messages.length <= 2 && (
                <div className="flex gap-2.5 max-w-[85%] animate-fadeIn">
                  <div className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-4 py-3 rounded-2xl rounded-tl-sm text-sm shadow-sm leading-relaxed border border-gray-200/25 dark:border-gray-700/25">
                    <p className="font-semibold text-teal-600 dark:text-teal-400 mb-0.5">Bentornato!</p>
                    <p>Continuiamo la nostra conversazione o hai altre domande sul mio profilo professionale?</p>
                  </div>
                </div>
              )}

              {/* Rendering della cronologia */}
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex gap-2.5 max-w-[85%] ${
                    msg.role === 'user' ? 'ml-auto flex-row-reverse' : ''
                  }`}
                >
                  <div
                    className={`px-4 py-2.5 text-sm leading-relaxed shadow-sm border ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-tr from-sky-500 to-teal-500 text-white rounded-2xl rounded-tr-sm border-sky-400/20'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-2xl rounded-tl-sm border-gray-200/20 dark:border-gray-700/20'
                    }`}
                  >
                    {msg.role === 'user' ? (
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    ) : (
                      <div className="prose prose-sm dark:prose-invert prose-p:leading-relaxed prose-a:text-sky-500 hover:prose-a:underline select-text">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    )}

                    {/* Feedback 👍/👎 per l'assistente */}
                    {msg.role === 'assistant' && msg.id && (
                      <div className="flex items-center gap-3 mt-2 pt-1.5 border-t border-gray-200/10 dark:border-gray-700/20 text-xs">
                        <span className="text-[10px] text-gray-400">Ti è stata utile?</span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleFeedback(msg.id!, true)}
                            disabled={!!feedbackStatus[msg.id]}
                            className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                              feedbackStatus[msg.id] === 'up' 
                                ? 'text-emerald-500 hover:text-emerald-500 font-bold' 
                                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                            }`}
                            title="Utile"
                          >
                            {feedbackStatus[msg.id] === 'up' ? (
                              <Check className="w-3.5 h-3.5" />
                            ) : (
                              <ThumbsUp className="w-3.5 h-3.5" />
                            )}
                          </button>
                          <button
                            onClick={() => handleFeedback(msg.id!, false)}
                            disabled={!!feedbackStatus[msg.id]}
                            className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                              feedbackStatus[msg.id] === 'down' 
                                ? 'text-red-500 hover:text-red-500 font-bold' 
                                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                            }`}
                            title="Non utile"
                          >
                            <ThumbsDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Indicatore di caricamento streaming */}
              {isLoading && (
                <div className="flex gap-2.5 max-w-[85%] animate-pulse">
                  <div className="bg-gray-100 dark:bg-gray-800 text-gray-400 px-4 py-3 rounded-2xl rounded-tl-sm text-sm border border-gray-200/20 dark:border-gray-700/20 flex items-center gap-2">
                    <Loader className="w-4 h-4 animate-spin text-teal-500" />
                    <span>Walter AI sta rispondendo...</span>
                  </div>
                </div>
              )}

              {/* Div di fine chat per scroll */}
              <div ref={messagesEndRef} />
            </div>

            {/* Suggerimenti dinamici (se la chat ha dei messaggi e non sta caricando) */}
            {messages.length > 0 && !isLoading && suggestions.length > 0 && (
              <div className="px-5 py-2.5 border-t border-gray-200/20 dark:border-gray-800/20 bg-gray-50/50 dark:bg-gray-900/50">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Domande frequenti suggerite
                </p>
                <div className="flex flex-wrap gap-1.5 max-h-[85px] overflow-y-auto">
                  {suggestions.map((sug, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        sendEvent('suggestion_click', sug);
                        handleSendMessage(sug);
                      }}
                      className="px-2.5 py-1 text-[11px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-sky-500/30 text-gray-600 dark:text-gray-300 hover:text-sky-500 dark:hover:text-sky-400 rounded-full transition-all duration-200 shadow-sm whitespace-nowrap"
                    >
                      {sug}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage(inputMessage);
              }}
              className="p-4 border-t border-gray-200/30 dark:border-gray-800/30 bg-white/70 dark:bg-gray-900/70 flex flex-col gap-2"
            >
              <div className="flex items-center gap-2 relative">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value.substring(0, 500))}
                  placeholder="Scrivi una domanda su Walter..."
                  disabled={isLoading}
                  maxLength={500}
                  className="flex-1 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-sm px-4 py-2.5 rounded-full pr-12 focus:outline-none focus:ring-2 focus:ring-sky-500/50 border border-transparent focus:border-sky-500 dark:focus:ring-sky-500/30 transition-all placeholder-gray-400"
                />
                
                <button
                  type="submit"
                  disabled={!inputMessage.trim() || isLoading}
                  className="absolute right-1.5 p-2 rounded-full bg-gradient-to-tr from-sky-500 to-teal-500 text-white shadow hover:scale-105 active:scale-95 disabled:opacity-0 disabled:scale-100 transition-all focus:outline-none"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>

              {/* Limite Caratteri */}
              <div className="flex items-center justify-between text-[10px] text-gray-400 px-2">
                <span>Massimo 500 caratteri</span>
                <span className={inputMessage.length >= 480 ? 'text-amber-500 font-semibold' : ''}>
                  {inputMessage.length}/500
                </span>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
